import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { CakeOutlined, EditOutlined, HomeOutlined } from "@mui/icons-material";
import toast from "react-hot-toast";
import {
  dayKey,
  formatDuration,
  formatMonthYear,
  ownershipDuration,
  toCalendarDay,
  toInputValue,
  todayDay,
} from "../../../../Utils/calendarDate";
import { DASH } from "../../dashboard/tokens";

type Source = "owner" | "public" | "claim";

type Props = {
  /** The owner's own "owned since" (wins when set). */
  ownedSince?: string | null;
  /** Public-record last sale date — usually the owner's purchase. */
  lastSaleDate?: string | null;
  /** When the claim was approved — the last resort. */
  claimedAt?: string | null;
  canEdit?: boolean;
  /** Persist a new date ("YYYY-MM-DD") or null to clear it. */
  onSave?: (value: string | null) => Promise<void>;
  /** "glass" sits on the photo header; "solid" stands alone (mobile). */
  variant?: "glass" | "solid";
};

/**
 * "Happy Home Anniversary!" (Asher, Sep 22). The ownership duration is always
 * shown; the celebratory treatment only appears during the anniversary month,
 * and only from the first anniversary on.
 *
 * Which date it counts from, in order: what the owner told us → the public
 * record's last sale → the claim approval. The owner can correct it, because
 * the public record is wrong for inherited or gifted homes.
 */
const HomeAnniversaryCard: React.FC<Props> = ({
  ownedSince,
  lastSaleDate,
  claimedAt,
  canEdit,
  onSave,
  variant = "glass",
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const picked = useMemo(() => {
    const today = todayDay();
    const claimDay = toCalendarDay(claimedAt);
    const candidates: { value?: string | null; source: Source }[] = [
      { value: ownedSince, source: "owner" },
      { value: lastSaleDate, source: "public" },
      { value: claimedAt, source: "claim" },
    ];
    for (const c of candidates) {
      const day = toCalendarDay(c.value);
      if (!day) continue;
      // A public-record sale AFTER the claim belongs to someone else's story.
      if (c.source === "public" && claimDay && dayKey(day) > dayKey(claimDay)) continue;
      const duration = ownershipDuration(day, today);
      if (duration) return { day, duration, source: c.source, raw: c.value as string, today };
    }
    return null;
  }, [ownedSince, lastSaleDate, claimedAt]);

  if (!picked) return null;
  const { duration, source, raw, today, day } = picked;
  const years = today.y - day.y;
  const celebrate = duration.isAnniversaryMonth;
  const isNew = duration.totalMonths === 0;
  const glass = variant === "glass";

  const sinceLabel = formatMonthYear(raw);
  const sourceNote =
    source === "public"
      ? `Since ${sinceLabel} · from public records`
      : source === "claim"
      ? `Counting from your claim in ${sinceLabel}`
      : `Since ${sinceLabel}`;

  const openEditor = () => {
    setDraft(toInputValue(day));
    setEditing(true);
  };

  const save = async (value: string | null) => {
    if (!onSave || saving) return;
    setSaving(true);
    try {
      await onSave(value);
      toast.success(value ? "Ownership date updated." : "Using the public record date again.");
      setEditing(false);
    } catch (e: any) {
      toast.error(e?.data?.errors?.message || e?.response?.data?.errors?.message || "Couldn't save that date.");
    } finally {
      setSaving(false);
    }
  };

  const fg = glass ? "#fff" : "#1d2b2b";
  const sub = glass ? "rgba(255,255,255,0.78)" : DASH.muted;

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        borderRadius: "16px",
        p: { xs: 4, md: 5 },
        width: "100%",
        maxWidth: { md: 320 },
        color: fg,
        ...(glass
          ? {
              bgcolor: "rgba(255,255,255,0.12)",
              border: "1px solid rgba(255,255,255,0.28)",
              backdropFilter: "blur(10px)",
            }
          : {
              bgcolor: celebrate ? DASH.cream : "#fff",
              border: `1px solid ${DASH.border}`,
              boxShadow: DASH.cardShadow,
            }),
      }}
      data-testid="home-anniversary-card"
    >
      {celebrate && <Confetti />}
      <Stack direction="row" spacing={3} alignItems="flex-start" sx={{ position: "relative" }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            flexShrink: 0,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: celebrate ? DASH.gold : glass ? "rgba(255,255,255,0.18)" : DASH.tealTint2,
            color: celebrate ? "#5b3a00" : glass ? "#fff" : DASH.teal,
          }}
        >
          {celebrate ? <CakeOutlined /> : <HomeOutlined />}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          {celebrate ? (
            <>
              <Typography sx={{ fontWeight: 800, fontSize: 18, lineHeight: 1.25 }} data-testid="anniversary-title">
                Happy Home Anniversary!
              </Typography>
              <Typography sx={{ mt: 1, fontSize: 14, color: sub }}>
                This month marks{" "}
                <Box component="strong" sx={{ color: fg }}>
                  {years} {years === 1 ? "year" : "years"}
                </Box>{" "}
                in your home.
              </Typography>
            </>
          ) : isNew ? (
            <>
              <Typography sx={{ fontWeight: 800, fontSize: 18 }}>Welcome home!</Typography>
              <Typography sx={{ mt: 1, fontSize: 14, color: sub }}>
                You've been a homeowner for less than a month.
              </Typography>
            </>
          ) : (
            <>
              <Typography sx={{ fontSize: 13, color: sub, fontWeight: 600 }}>
                You've been a homeowner for
              </Typography>
              <Typography sx={{ fontWeight: 800, fontSize: 20, lineHeight: 1.25, mt: 0.5 }} data-testid="ownership-duration">
                {formatDuration(duration)}
              </Typography>
            </>
          )}
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 2, flexWrap: "wrap" }}>
            <Typography sx={{ fontSize: 12, color: sub }}>{sourceNote}</Typography>
            {canEdit && onSave && (
              <Button
                size="small"
                onClick={openEditor}
                startIcon={<EditOutlined sx={{ fontSize: "14px !important" }} />}
                sx={{
                  minWidth: 0,
                  px: 1,
                  py: 0,
                  fontSize: 12,
                  fontWeight: 700,
                  color: glass ? "#fff" : DASH.teal,
                  textDecoration: "underline",
                  "&:hover": { textDecoration: "underline", bgcolor: "transparent" },
                }}
                data-testid="edit-owned-since"
              >
                {source === "owner" ? "Edit" : "Not right?"}
              </Button>
            )}
          </Stack>
        </Box>
      </Stack>

      <Dialog open={editing} onClose={saving ? undefined : () => setEditing(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: DASH.teal }}>When did your home become yours?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: DASH.muted, mb: 3 }}>
            We start from the public record's last sale date. If you inherited the home, or the
            record is off, set the date you actually took ownership.
          </Typography>
          <TextField
            type="date"
            label="Owned since"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: toInputValue(todayDay()), min: "1900-01-01", "data-testid": "owned-since-input" }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 6, pb: 4, justifyContent: "space-between" }}>
          {source === "owner" ? (
            <Button onClick={() => save(null)} disabled={saving} sx={{ color: DASH.muted }}>
              Reset
            </Button>
          ) : (
            <span />
          )}
          <Stack direction="row" spacing={1}>
            <Button onClick={() => setEditing(false)} disabled={saving} sx={{ color: DASH.muted }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => save(draft)}
              disabled={saving || !draft}
              sx={{ bgcolor: DASH.teal, "&:hover": { bgcolor: DASH.tealDark } }}
              data-testid="save-owned-since"
            >
              {saving ? "Saving…" : "Save"}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

/** A few soft confetti flecks — decoration only, hidden from assistive tech. */
const Confetti = () => {
  const flecks = [
    { top: 10, left: "62%", c: "#fbbe68", r: 18 },
    { top: 26, left: "84%", c: "#35AC8C", r: -24 },
    { top: 58, left: "92%", c: "#f28b82", r: 40 },
    { top: 8, left: "40%", c: "#9ad0c2", r: 12 },
    { top: 72, left: "76%", c: "#fbbe68", r: -8 },
  ];
  return (
    <Box aria-hidden sx={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
      {flecks.map((f, i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            top: f.top,
            left: f.left,
            width: 8,
            height: 4,
            borderRadius: "2px",
            bgcolor: f.c,
            transform: `rotate(${f.r}deg)`,
            opacity: 0.9,
          }}
        />
      ))}
    </Box>
  );
};

export default HomeAnniversaryCard;
